import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";

import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import AuthService from "../../service/auth";
import { showToast } from "../../store/actions";
import { regexPassword } from "../../util/regularExpress";
import { PasswordValidationText } from "../Common/Common";

function ResetPassword() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({
    password: false,
    confirmPassword: false
  });

  useEffect(() => {
    if (token) {
      const auth = new AuthService();
      auth
        .getUserByPasswordResetToken(token)
        .then((result) => {
          if (result.success) setLoginId(result.user);
          else navigate("/");
        })
        .catch(() => navigate("/"));
    }

    return () => { };
  }, [token, navigate]);

  const handleShowPassword = (e, name) => {
    setShowPassword((prevState) => {
      return {
        ...prevState,
        [name]: !prevState[name],
      };
    });
  };

  const handleSendLink = async (e) => {
    e.preventDefault();

    setErrors((prevState) => {
      return {
        ...prevState,
        password: !regexPassword(password.trim()),
        confirmPassword: password.trim() !== confirmPassword.trim()
      };
    });

    if (
      !(
        !regexPassword(password.trim()) ||
        password.trim() !== confirmPassword.trim()
      )
    ) {
      setLoading(true);

      const authService = new AuthService();
      const response = await authService.resetPassword(token, password);
      if (response.success) {
        dispatch(
          showToast({
            type: "success",
            message: response.message,
          })
        );

        navigate("/");
      }
      setLoginId("");
      setPassword("");
      setConfirmPassword("");
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Box
        component="main"
        sx={{
          background: (theme) => {
            return theme.palette.TiffanyBlue.gradient;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
            }}
          >
            <Box
              component="form"
              noValidate
              sx={{ width: "80%" }}
              style={{ background: "#fff", padding: "25px", borderRadius: "10px" }}
            >
              <Typography style={{ fontSize: "28px", fontWeight: "bold", textAlign: "center" }}>
                RESET YOUR PASSWORD
              </Typography>

              <hr />

              <Typography style={{ fontSize: "16px" }}>Login ID: {loginId}</Typography>

              <TextField
                type={showPassword.new ? "text" : "password"}
                margin="normal"
                required
                fullWidth
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="******"
                helperText={errors.password ? <PasswordValidationText /> : ""}
                error={errors.password}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={(e) => handleShowPassword(e, "new")}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                autoComplete={"off"}
              />

              <TextField
                type={showPassword.confirm ? "text" : "password"}
                margin="normal"
                required
                fullWidth
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="******"
                error={errors.confirmPassword}
                helperText={errors.confirmPassword ? "Passwords does not match" : ""}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={(e) => handleShowPassword(e, "confirm")}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
                autoComplete={"off"}
              />

              <Button
                color="secondary"
                onClick={handleSendLink}
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={!(password.trim() && confirmPassword.trim())}
              >
                Reset Password
              </Button>
              <Link
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  fontSize: "15px",
                  textDecoration: "none",
                  color: "#514efd",
                }}
                to="/"
              >
                <ArrowBackIcon fontSize="small" />
                Back to login
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default ResetPassword;
